/**
 * Product Grid Wishlist Handler
 * Handles wishlist functionality for product grid items
 */

// Wishlist API Configuration (from product-images.liquid)
const WISHLIST_CONFIG = {
  baseUrl: 'https://wishlist.webcontrive.com/wishlist-club/public',
  token: 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJodHRwczovL3dpc2hsaXN0LndlYmNvbnRyaXZlLmNvbS93aXNobGlzdC1jbHViL3B1YmxpYyIsImlhdCI6MTc2NTg4MzU4MiwibmJmIjoxNzY1ODgzNTgyLCJleHAiOjE3OTc0MTk1ODIsInNob3BfaWQiOjQxODF9.B3w22Jpa5klhom2uJVjP3XapvnCLTDhmSuequs_rQTY',
  shop: 'sicotas.myshopify.com',
  maxItems: 30 // Maximum items allowed in wishlist
};

// Storage key for guest wishlist
const GUEST_WISHLIST_KEY = 'guestWishlist';
const SYNC_FLAG_KEY = 'wishlistSyncCompleted';

/**
 * Initialize wishlist functionality
 */
function initProductGridWishlist() {
  // Event delegation for wishlist buttons
  document.addEventListener('click', async (e) => {
    const wishlistBtn = e.target.closest('.product-grid-wishlist');
    if (wishlistBtn) {
      e.preventDefault();
      e.stopPropagation();
      await handleWishlistClick(wishlistBtn);
    }
  });

  // Sync localStorage to API on page load if user is logged in
  syncGuestWishlistOnLogin();
}

/**
 * Handle wishlist button click
 * @param {HTMLElement} button - The wishlist button element
 */
async function handleWishlistClick(button) {
  try {
    // Get product data from button
    const productId = button.dataset.productId;
    const variantId = button.dataset.variantId;
    const productHandle = button.dataset.productHandle;

    if (!productId || !variantId) {
      console.error('Missing product or variant ID');
      showNotification('Invalid product data', 'error');
      return;
    }

    // Disable button during processing and show loading
    button.disabled = true;
    button.classList.add('is-loading');

    // Check if user is logged in
    const customerId = getCustomerId();
    
    if (!customerId) {
      // Handle unauthenticated user - store in localStorage
      const guestWishlist = getGuestWishlist();
      
      // Check 30-item limit for guest
      if (guestWishlist.length >= WISHLIST_CONFIG.maxItems) {
        showNotification(`You can only save up to ${WISHLIST_CONFIG.maxItems} items in your wishlist`, 'error');
        button.disabled = false;
        button.classList.remove('is-loading');
        return;
      }

      // Check if item already exists in guest wishlist
      const exists = guestWishlist.some(item => 
        item.productId === productId && item.variantId === variantId
      );

      if (exists) {
        showNotification('Item already in wishlist', 'info');
        button.disabled = false;
        button.classList.remove('is-loading');
        return;
      }

      addToLocalStorageWishlist(productId, variantId, productHandle);
      
      // Use Figma toast for success
      showWishlistSuccessToast(productHandle);
      
      button.classList.add('is-active');
      button.classList.remove('is-loading');
      button.disabled = false;
      return;
    }

    // For logged-in users: check current wishlist count
    const currentCount = await getWishlistItemCount(customerId);
    
    if (currentCount >= WISHLIST_CONFIG.maxItems) {
      // 已登录用户：打开心愿单侧滑并显示Full状态
      if (window.WishlistDrawer && typeof window.WishlistDrawer.openWithFullState === 'function') {
        window.WishlistDrawer.openWithFullState();
      } else {
        // Fallback to notification
        showNotification(`You have reached the maximum of ${WISHLIST_CONFIG.maxItems} items in your wishlist`, 'error');
      }
      button.disabled = false;
      button.classList.remove('is-loading');
      return;
    }

    // Check if item already in wishlist
    const isInWishlist = await checkIfInWishlist(productId, customerId);
    if (isInWishlist) {
      // Show "Already Saved" toast
      if (window.WishlistAlreadySaved && typeof window.WishlistAlreadySaved.show === 'function') {
        window.WishlistAlreadySaved.show();
      } else {
        showNotification('Item already in wishlist', 'info');
      }
      button.classList.add('is-active');
      button.classList.remove('is-loading');
      button.disabled = false;
      return;
    }

    // Add to wishlist via API
    const result = await addToWishlistAPI(productId, variantId, customerId);
    
    if (result.success) {
      // Use Figma toast for success
      showWishlistSuccessToast(productHandle);
      
      button.classList.add('is-active');
      button.classList.remove('is-loading');
      
      // Refresh wishlist count from API
      refreshWishlistCount(customerId);
    } else {
      showNotification(result.message || 'Failed to add to wishlist', 'error');
      button.classList.remove('is-loading');
    }
    
  } catch (error) {
    console.error('Error adding to wishlist:', error);
    
    // Show user-friendly error message
    if (error.message.includes('network') || error.message.includes('fetch')) {
      showNotification('Network error. Please check your connection', 'error');
    } else if (error.message.includes('already exists')) {
      showNotification('Item already in wishlist', 'info');
    } else {
      showNotification('Failed to add to wishlist. Please try again', 'error');
    }
    
    button.classList.remove('is-loading');
  } finally {
    button.disabled = false;
  }
}

/**
 * Get or create wishlist folder for user
 * @param {string} customerId - Customer ID
 * @returns {Promise<number|null>} Wishlist ID or null
 */
async function getOrCreateWishlistFolder(customerId) {
  const getUrl = `${WISHLIST_CONFIG.baseUrl}/api/v3/get`;
  
  try {
    // 先尝试获取现有的 wishlist
    const getResponse = await fetch(getUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain',
        'Authorization': `Bearer ${WISHLIST_CONFIG.token}`
      },
      body: JSON.stringify({
        shop: WISHLIST_CONFIG.shop,
        customer_id: customerId ? String(customerId) : null,
        ip: null
      })
    });
    
    if (getResponse.ok) {
      const data = await getResponse.json();
      if (data.status === 200 && data.data?.wishlists?.length > 0) {
        // 已有 wishlist，返回第一个的 ID
        return data.data.wishlists[0].id;
      }
    }
    
    // 没有 wishlist，创建一个
    const createUrl = `${WISHLIST_CONFIG.baseUrl}/api/v3/wishlist`;
    const createResponse = await fetch(createUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain',
        'Authorization': `Bearer ${WISHLIST_CONFIG.token}`
      },
      body: JSON.stringify({
        shop: WISHLIST_CONFIG.shop,
        name: 'My Wishlist',
        customer_id: customerId ? String(customerId) : null,
        ip: null
      })
    });
    
    if (createResponse.ok) {
      const createData = await createResponse.json();
      if (createData.status === 200 && createData.data) {
        return createData.data.id;
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error getting/creating wishlist folder:', error);
    return null;
  }
}

/**
 * Add item to wishlist via API
 * @param {string} productId - Product ID
 * @param {string} variantId - Variant ID
 * @param {string} customerId - Customer ID
 * @returns {Promise<Object>} Result object with success status and message
 */
async function addToWishlistAPI(productId, variantId, customerId) {
  // 先获取或创建 wishlist folder
  const wishlistId = await getOrCreateWishlistFolder(customerId);
  if (!wishlistId) {
    throw new Error('Failed to get or create wishlist folder');
  }
  
  const url = `${WISHLIST_CONFIG.baseUrl}/api/v3/add`;
  
  const payload = {
    customer_id: customerId ? String(customerId) : null,
    ip: null,
    shop: WISHLIST_CONFIG.shop,
    wishlist_id: wishlistId,
    product_id: productId,
    variant_id: variantId
  };

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain',
        'Authorization': `Bearer ${WISHLIST_CONFIG.token}`
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    if (!response.ok || data.status !== 200) {
      // Handle specific API errors
      if (data.status === 422 || data.message?.includes('already')) {
        return { success: false, message: 'Item already in wishlist' };
      }
      if (data.status === 400) {
        return { success: false, message: data.message || 'Invalid request' };
      }
      throw new Error(`API request failed: ${response.status}`);
    }

    return { success: true, data };
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
}

/**
 * Check if product is already in wishlist
 * @param {string} productId - Product ID
 * @param {string} customerId - Customer ID
 * @returns {Promise<boolean>} True if product is in wishlist
 */
async function checkIfInWishlist(productId, customerId) {
  const url = `${WISHLIST_CONFIG.baseUrl}/api/v3/get`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain',
        'Authorization': `Bearer ${WISHLIST_CONFIG.token}`
      },
      body: JSON.stringify({
        shop: WISHLIST_CONFIG.shop,
        customer_id: String(customerId),
        ip: null
      })
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    
    // Check if product exists in wishlist
    if (data.status === 200 && data.data) {
      const wishlists = data.data.wishlists || [];
      
      for (const list of wishlists) {
        const items = list.wishlists || [];
        if (items.some(item => String(item.shopify_product_id) === String(productId))) {
          return true;
        }
      }
    }

    return false;
  } catch (error) {
    console.error('Error checking wishlist:', error);
    return false;
  }
}

/**
 * Get current wishlist item count
 * @param {string} customerId - Customer ID
 * @returns {Promise<number>} Number of items in wishlist
 */
async function getWishlistItemCount(customerId) {
  const url = `${WISHLIST_CONFIG.baseUrl}/api/v3/get`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain',
        'Authorization': `Bearer ${WISHLIST_CONFIG.token}`
      },
      body: JSON.stringify({
        shop: WISHLIST_CONFIG.shop,
        customer_id: String(customerId),
        ip: null
      })
    });

    if (!response.ok) {
      console.error('Failed to fetch wishlist count');
      return 0; // Return 0 on error to allow operation to continue
    }

    const data = await response.json();
    
    // Handle API response structure
    if (data.status === 200 && data.data) {
      const wishlists = data.data.wishlists || [];
      let totalCount = 0;
      
      for (const list of wishlists) {
        const items = list.wishlists || [];
        totalCount += items.length;
      }
      
      return totalCount;
    }

    return 0;
  } catch (error) {
    console.error('Error fetching wishlist count:', error);
    return 0; // Return 0 on error
  }
}

/**
 * Get customer ID from Shopify
 * @returns {string|null} Customer ID or null if not logged in
 */
function getCustomerId() {
  // Check Shopify Analytics
  if (window.__st && window.__st.cid) {
    return String(window.__st.cid);
  }

  // Check ShopifyAnalytics
  if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.page) {
    const cid = window.ShopifyAnalytics.meta.page.customerId;
    return cid ? String(cid) : null;
  }

  return null;
}

/**
 * Add to localStorage for unauthenticated users
 * @param {string} productId - Product ID
 * @param {string} variantId - Variant ID
 * @param {string} productHandle - Product handle
 */
function addToLocalStorageWishlist(productId, variantId, productHandle) {
  let wishlist = getGuestWishlist();

  // Check if item already exists
  const exists = wishlist.some(item => 
    item.productId === productId && item.variantId === variantId
  );

  if (!exists) {
    wishlist.push({
      productId,
      variantId,
      productHandle,
      timestamp: Date.now()
    });

    try {
      localStorage.setItem(GUEST_WISHLIST_KEY, JSON.stringify(wishlist));
    } catch (e) {
      console.error('Error writing to localStorage:', e);
      throw new Error('Failed to save to wishlist');
    }
  }
}

/**
 * Get guest wishlist from localStorage
 * @returns {Array} Array of wishlist items
 */
function getGuestWishlist() {
  try {
    const stored = localStorage.getItem(GUEST_WISHLIST_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (e) {
    console.error('Error reading from localStorage:', e);
    return [];
  }
}

/**
 * Clear guest wishlist from localStorage
 */
function clearGuestWishlist() {
  try {
    localStorage.removeItem(GUEST_WISHLIST_KEY);
  } catch (e) {
    console.error('Error clearing localStorage:', e);
  }
}

/**
 * Sync guest wishlist to API when user logs in
 */
async function syncGuestWishlistOnLogin() {
  const customerId = getCustomerId();
  
  // Only sync if user is logged in and hasn't been synced yet
  if (!customerId) {
    return;
  }

  // Check if already synced in this session
  const syncFlag = sessionStorage.getItem(SYNC_FLAG_KEY);
  if (syncFlag === 'true') {
    return;
  }

  const guestWishlist = getGuestWishlist();
  
  if (guestWishlist.length === 0) {
    sessionStorage.setItem(SYNC_FLAG_KEY, 'true');
    return;
  }

  console.log(`Syncing ${guestWishlist.length} items from guest wishlist to API...`);

  try {
    // Get current wishlist count
    const currentCount = await getWishlistItemCount(customerId);
    let syncedCount = 0;
    let skippedCount = 0;

    // Sync items one by one
    for (const item of guestWishlist) {
      // Check if we've reached the limit
      if (currentCount + syncedCount >= WISHLIST_CONFIG.maxItems) {
        console.log(`Reached maximum limit of ${WISHLIST_CONFIG.maxItems} items`);
        skippedCount = guestWishlist.length - syncedCount;
        break;
      }

      try {
        const result = await addToWishlistAPI(item.productId, item.variantId, customerId);
        if (result.success) {
          syncedCount++;
        }
      } catch (error) {
        console.error(`Failed to sync item ${item.productId}:`, error);
        // Continue with next item
      }

      // Add small delay to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Clear guest wishlist after successful sync
    clearGuestWishlist();
    
    // Mark as synced
    sessionStorage.setItem(SYNC_FLAG_KEY, 'true');

    // Refresh wishlist count from API
    await refreshWishlistCount(customerId);

    // Show notification to user
    if (syncedCount > 0) {
      showNotification(`${syncedCount} item(s) added to your wishlist`);
    }

    if (skippedCount > 0) {
      setTimeout(() => {
        showNotification(`${skippedCount} item(s) skipped due to ${WISHLIST_CONFIG.maxItems}-item limit`, 'info');
      }, 3500);
    }

    console.log(`Sync completed: ${syncedCount} synced, ${skippedCount} skipped`);
  } catch (error) {
    console.error('Error syncing guest wishlist:', error);
    // Don't clear localStorage on error, so user can try again
  }
}

/**
 * Refresh wishlist count from API and update header
 * @param {string} customerId - Customer ID
 */
async function refreshWishlistCount(customerId) {
  if (!customerId) return;
  
  try {
    const count = await getWishlistItemCount(customerId);
    updateWishlistCount(count);
    
    // Refresh cart page wishlist section if it exists
    if (window.CartWishlist && typeof window.CartWishlist.loadWishlistData === 'function') {
      console.log('🔄 Refreshing cart page wishlist section...');
      await window.CartWishlist.loadWishlistData();
    }
  } catch (error) {
    console.error('Error refreshing wishlist count:', error);
  }
}

/**
 * Update wishlist count in header
 * @param {number} count - New count
 */
function updateWishlistCount(count) {
  // Update bubble number
  const headerBubbleNum = document.querySelector('.wishlist-link__bubble-num');
  if (headerBubbleNum) {
    headerBubbleNum.textContent = count;
  }

  // Show/hide bubble based on count
  const headerBubble = document.querySelector('.wishlist-link__bubble');
  if (headerBubble) {
    if (count > 0) {
      headerBubble.classList.add('wishlist-link__bubble--visible');
    } else {
      headerBubble.classList.remove('wishlist-link__bubble--visible');
    }
  }

  // Also try to update other count elements
  const countElements = document.querySelectorAll('.wishlist-count, [data-wishlist-count]');
  countElements.forEach(el => {
    el.textContent = count;
    
    if (count > 0) {
      el.style.display = '';
    } else {
      el.style.display = 'none';
    }
  });
}

/**
 * Show wishlist success toast (Figma design)
 * @param {string} productHandle - Product handle to fetch product data
 */
function showWishlistSuccessToast(productHandle) {
  // Use the global WishlistToast from the theme if available
  if (window.WishlistToast && typeof window.WishlistToast.show === 'function') {
    // Try to get product info from the page
    const productCard = document.querySelector(`[data-product-handle="${productHandle}"]`);
    let productData = {};
    
    if (productCard) {
      const titleEl = productCard.querySelector('.grid-product__title, .product-title');
      const imageEl = productCard.querySelector('img');
      
      const priceContainer = productCard.querySelector('.grid-product__price');
      const originalPriceEl = priceContainer?.querySelector('.grid-product__price--original');
      const currentPriceEl = priceContainer?.querySelector('.grid-product__price--current');
      
      let priceHtml = '';
      if (originalPriceEl && currentPriceEl) {
        // 有促销价，获取第一个非隐藏的价格元素
        const originalPrice = originalPriceEl.querySelector('[aria-hidden="true"]')?.textContent?.trim() || originalPriceEl.textContent.trim();
        const currentPrice = currentPriceEl.querySelector('[aria-hidden="true"]')?.textContent?.trim() || currentPriceEl.textContent.trim();
        priceHtml = `<span class="toast-price-compare">${originalPrice}</span><span class="toast-price-sale">${currentPrice}</span>`;
      } else if (currentPriceEl) {
        // 只有一个价格，获取第一个非隐藏的价格元素
        const currentPrice = currentPriceEl.querySelector('[aria-hidden="true"]')?.textContent?.trim() || currentPriceEl.textContent.trim();
        priceHtml = `<span class="toast-price-regular">${currentPrice}</span>`;
      }
      
      productData = {
        title: titleEl?.textContent?.trim() || '',
        image: imageEl?.src || '',
        price: priceHtml
      };
    }
    
    window.WishlistToast.show(productData);
  } else {
    // Fallback to simple notification if toast not available
    showNotification('Item added to wishlist');
  }
}

/**
 * Show notification to user
 * @param {string} message - Notification message
 * @param {string} type - Notification type (success, error, info)
 */
function showNotification(message, type = 'success') {
  // Remove any existing notifications
  const existingNotifications = document.querySelectorAll('.wishlist-notification');
  existingNotifications.forEach(n => n.remove());

  // Create notification element
  const notification = document.createElement('div');
  notification.className = `wishlist-notification wishlist-notification--${type}`;
  notification.textContent = message;
  
  // Add to document
  document.body.appendChild(notification);
  
  // Show notification
  setTimeout(() => {
    notification.classList.add('is-visible');
  }, 10);
  
  // Hide and remove after 3 seconds
  setTimeout(() => {
    notification.classList.remove('is-visible');
    setTimeout(() => {
      notification.remove();
    }, 300);
  }, 3000);
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductGridWishlist);
} else {
  initProductGridWishlist();
}
